'use client';

import Icon from '@/components/ui/AppIcon';

interface Collaborator {
  name: string;
  focus: string;
  description: string;
  keyServices: string[];
  impact: string[];
  icon: string;
  color: string;
  website?: string;
}

const KeyCollaboratorsSection = () => {
  const collaborators: Collaborator[] = [
    {
      name: 'Prime Equation',
      focus: 'Banking and Government Initiatives',
      description: 'Strategic partner specializing in financial sector ESG compliance, regulatory frameworks, and government sustainability programs. Expert in banking carbon footprint assessment and green finance solutions.',
      keyServices: [
        'Risk Management',
        'Data Collection',
      ],
      impact: [
        'Regulatory Compliance',
        'Financial Sector Transformation',
        'Government Engagement',
      ],
      icon: 'BuildingLibraryIcon',
      color: 'primary',
    },
    {
      name: 'Upstream Solutions',
      focus: 'Water and Environment',
      description: 'Leading environmental technology partner providing comprehensive water conservation, ecosystem restoration, and environmental monitoring solutions using advanced technology and data analytics.',
      keyServices: [
        'Water Conservation Technology',
        'Ecosystem Restoration Projects',
        'Environmental Impact Assessment',
        'Watershed Management',
        'Water Quality Monitoring',
        'Sustainable Water Infrastructure',
      ],
      impact: [
        'Water Resource Management',
        'Ecosystem Health',
        'Environmental Protection',
      ],
      icon: 'BeakerIcon',
      color: 'secondary',
    },
    {
      name: 'Biotas Organo Fisheries',
      focus: 'Shrimp Farm Sustainable Exports AI Algorithms',
      description: 'Innovative aquaculture partner leveraging AI and machine learning for sustainable shrimp farming, optimizing production efficiency while minimizing environmental impact and ensuring export quality compliance.',
      keyServices: [
        'AI-Powered Aquaculture Optimization',
        'Sustainable Shrimp Farming',
        'Export Quality Assurance',
        'Water Quality AI Monitoring',
        'Disease Prevention Algorithms',
        'Carbon Footprint Tracking',
      ],
      impact: [
        'Sustainable Aquaculture',
        'Export Excellence',
        'AI-Driven Innovation',
      ],
      icon: 'CpuChipIcon',
      color: 'accent',
    },
    {
      name: 'Clean Water Initiative',
      focus: 'Water Sustainability and Conservation',
      description: 'Dedicated to advancing clean water access and conservation through innovative technologies, community engagement, and sustainable water management practices across India.',
      keyServices: [
        'Water Purification Solutions',
        'Community Water Projects',
        'Water Conservation Technology',
        'Sustainable Infrastructure',
      ],
      impact: [
        'Clean Water Access',
        'Community Health',
        'Water Security',
      ],
      icon: 'BeakerIcon',
      color: 'secondary',
      website: 'https://clean-water.co.in/',
    },
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      primary: {
        bg: 'bg-primary/10',
        text: 'text-primary',
        badge: 'bg-primary/10 text-primary',
        hover: 'hover:bg-primary',
      },
      secondary: {
        bg: 'bg-secondary/10',
        text: 'text-secondary',
        badge: 'bg-secondary/10 text-secondary',
        hover: 'hover:bg-secondary',
      },
      accent: {
        bg: 'bg-accent/10',
        text: 'text-accent',
        badge: 'bg-accent/10 text-accent',
        hover: 'hover:bg-accent',
      },
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4">
            Key Collaborators
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our strategic partners bring specialized expertise across critical sectors, 
            enabling comprehensive sustainability solutions and measurable environmental impact.
          </p>
        </div>

        {/* Collaborators Grid */}
        <div className="space-y-8">
          {collaborators.map((collaborator, index) => {
            const colorClasses = getColorClasses(collaborator.color);
            return (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-8 hover:shadow-natural-xl transition-all duration-500 ease-organic group"
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Left Column - Overview */}
                  <div className="lg:col-span-1">
                    <div className={`w-16 h-16 ${colorClasses.bg} rounded-xl flex items-center justify-center mb-4 ${colorClasses.hover} group-hover:scale-110 transition-all duration-300`}>
                      <Icon name={collaborator.icon} size={32} className={`${colorClasses.text} group-hover:text-white transition-colors`} />
                    </div>
                    <h3 className={`text-2xl font-heading font-bold text-text-primary mb-2 group-hover:${colorClasses.text} transition-colors`}>
                      {collaborator.name}
                    </h3>
                    <p className={`font-semibold ${colorClasses.text} mb-4`}>
                      {collaborator.focus}
                    </p>
                    <p className="text-sm text-text-secondary leading-relaxed mb-4">
                      {collaborator.description}
                    </p>
                    {collaborator.website && (
                      <a
                        href={collaborator.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center space-x-2 text-sm ${colorClasses.text} hover:underline`}
                      >
                        <Icon name="GlobeAltIcon" size={16} />
                        <span>Visit Website</span>
                        <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                      </a>
                    )}
                  </div>

                  {/* Right Column - Impact Areas Only */}
                  <div className="lg:col-span-1">
                    <h4 className="font-heading font-bold text-text-primary mb-4 flex items-center space-x-2">
                      <Icon name="SparklesIcon" size={20} className={colorClasses.text} />
                      <span>Impact Areas</span>
                    </h4>
                    <div className="space-y-3">
                      {collaborator.impact.map((area, idx) => (
                        <div
                          key={idx}
                          className={`px-4 py-3 ${colorClasses.badge} rounded-lg font-medium text-sm`}
                        >
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default KeyCollaboratorsSection;